import React, { useState, useEffect, useCallback, useContext, useRef, memo } from 'react';
import { useRemountOnPageHidden } from '../../hooks/useRemountOnPageHidden';
import {
  IonContent,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardSubtitle,
  IonCardContent,
  IonIcon,
  useIonRouter,
  IonPage,
  IonGrid,
  IonRow,
  IonCol,
  IonHeader,
  IonToolbar,
  IonAvatar,
  IonButtons,
  IonButton,
  IonItem,
  IonLabel,
  IonText,
  IonModal,
  IonList,
  useIonViewWillEnter,
  IonSkeletonText,
  IonBadge,

} from '@ionic/react';
import {
  calendar,
  chatbubblesOutline,
  chevronForward,
  cash,
  trendingUp,
  personAdd,
  checkmarkCircle,
  alertCircle,
  barChart,
  receiptOutline,
  warningOutline,
  notificationsOutline,
  callOutline,
  calendarOutline,
} from 'ionicons/icons';
import { HiOutlinePencil } from "react-icons/hi2";
import { useAuth } from '../../contexts';
import { AuthContext } from '../../contexts/auth-context-core';
import { UserInfo as _User } from '@rsmaids/shared/types/auth';
import styles from './Dashboard.module.scss';
import { ShortcutsEditModal } from '../../components/ShortcutsEditModal';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import { DataServiceFactory } from '@rsmaids/shared/services/DataServiceFactory';
import { getBookingsFromCache, subscribeToUpcomingBookings } from '@rsmaids/shared/services/api/bookingsCacheService';
import { contactsCache } from '@rsmaids/shared/services/api/contactsCacheService';
import { BookingItem } from '@rsmaids/shared/types/bookings';
import { BusinessStats, Activity } from '@rsmaids/shared/types/analytics';
import { Shortcut } from '@rsmaids/shared/types/dashboard';
import { OWNER_SHORTCUTS, OWNER_CATEGORY_NAMES } from './shortcutCatalogues';
import { getDefaultAvatar, proxyImage } from '@rsmaids/shared/utils/avatarUtils';
import { getServiceColor, ServiceType } from '@rsmaids/shared/types/services';
import BookingDetails from '../bookingSystem/BookingDetails';
import { DataStorageService } from '@rsmaids/shared/services/DataStorageService';
import { getZohoInvoices, getZohoProfitAndLossIncome } from '../../services/zohoInvoicingService';
import type { ZohoInvoice } from '../../services/zohoInvoicingService';
import ActivityGroupPage from './ActivityGroupPage';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { intlLocale, getMonthNames, getWeekdayNames } from '../../i18n/locale';
import { translateDynamic } from '../../i18n/dynamic';
import { localizeActivity } from '../../utils/activityI18n';

function formatActivityTime(dateStr: string | undefined): string {
  if (!dateStr) return i18n.t('pages.dashboard.DashboardOwners.recently');

  let date: Date;
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const [year, month, day] = dateStr.split('-').map(Number);
    date = new Date(year, (month || 1) - 1, day || 1);
  } else {
    date = new Date(dateStr);
  }

  if (isNaN(date.getTime())) return i18n.t('pages.dashboard.DashboardOwners.recently');
  const diffMs = Date.now() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 2) return i18n.t('pages.dashboard.DashboardOwners.justNow');
  if (diffMins < 60) return i18n.t('activity.minutesAgo', { n: diffMins });
  if (diffHours < 24) return i18n.t('activity.hoursAgo', { n: diffHours });
  if (diffDays === 1) return i18n.t('pages.dashboard.DashboardOwners.yesterday');
  if (diffDays < 7) return i18n.t('activity.daysAgo', { n: diffDays });
  return date.toLocaleDateString(intlLocale(), { month: 'short', day: 'numeric' });
}

function zohoInvoiceToActivity(inv: ZohoInvoice): Activity {
  const clientName = inv.customer_name || 'Client';
  const amount = inv.total ? `${Number(inv.total).toFixed(2)} USD` : '';
  const invoiceNum = inv.invoice_number ? `Invoice #${inv.invoice_number}` : '';
  const userInitial = clientName.charAt(0).toUpperCase();
  const status = (inv.status || '').toLowerCase();
  const time = formatActivityTime(inv.last_modified_time || inv.created_time || inv.date);

  if (status === 'paid') {
    return {
      id: `zoho-paid-${inv.invoice_id}`,
      type: 'payment',
      title: 'Payment Received',
      detail: [invoiceNum, amount ? `for ${amount}` : '', clientName ? `paid by ${clientName}` : ''].filter(Boolean).join(' '),
      time,
      userInitial,
    };
  }
  if (status === 'overdue') {
    return {
      id: `zoho-overdue-${inv.invoice_id}`,
      type: 'overdue',
      title: 'Payment Overdue',
      detail: [invoiceNum, amount ? `for ${amount}` : '', clientName ? `— ${clientName}` : ''].filter(Boolean).join(' '),
      time,
      userInitial,
    };
  }
  if (status === 'draft') {
    return {
      id: `zoho-draft-${inv.invoice_id}`,
      type: 'invoice',
      title: 'Invoice Draft',
      detail: [invoiceNum, amount ? `for ${amount}` : '', clientName ? `— ${clientName}` : ''].filter(Boolean).join(' '),
      time,
      userInitial,
    };
  }
  if (status === 'viewed') {
    return {
      id: `zoho-viewed-${inv.invoice_id}`,
      type: 'invoice',
      title: 'Invoice Viewed',
      detail: [invoiceNum, amount ? `for ${amount}` : '', clientName ? `— ${clientName}` : ''].filter(Boolean).join(' '),
      time,
      userInitial,
    };
  }
  if (status === 'void') {
    return {
      id: `zoho-void-${inv.invoice_id}`,
      type: 'invoice',
      title: 'Invoice Voided',
      detail: [invoiceNum, amount ? `for ${amount}` : '', clientName ? `— ${clientName}` : ''].filter(Boolean).join(' '),
      time,
      userInitial,
    };
  }
  return {
    id: `zoho-invoice-${inv.invoice_id}`,
    type: 'invoice',
    title: status === 'sent' ? 'Invoice Sent' : 'Invoice Created',
    detail: [invoiceNum, amount ? `for ${amount}` : '', clientName ? `— ${clientName}` : ''].filter(Boolean).join(' '),
    time,
    userInitial,
  };
}

function getContactAvatarUrl(contact: { avatarUrl?: string; avatar?: string; picture?: string; info?: { picture?: string } } | null | undefined): string | undefined {
  if (!contact) return undefined;
  if (typeof contact.avatarUrl === 'string' && contact.avatarUrl.trim() !== '') return contact.avatarUrl;
  if (typeof contact.avatar === 'string' && contact.avatar.trim() !== '') return contact.avatar;
  if (typeof contact.picture === 'string' && contact.picture.trim() !== '') return contact.picture;
  if (contact.info && typeof contact.info.picture === 'string' && contact.info.picture.trim() !== '') return contact.info.picture;
  return undefined;
}

const DashboardOwners: React.FC = () => {
  const { t: tr } = useTranslation();
  const router = useIonRouter();
  // Badge = notifications that arrived since the alerts were last opened (from the
  // context's already-loaded newest notifications — no separate fetch or polling).
  const { authState, unreadCount: unreadNotificationsCount } = useAuth();
  const { user } = authState;
  const authContext = useContext(AuthContext);

  // The scrolling area is rebuilt while the page is hidden, so it is back at the top on return
  const scrollableContentRef = useRef<HTMLIonGridElement>(null);
  const scrollResetKey = useRemountOnPageHidden(scrollableContentRef);

  const [isEditMode, setIsEditMode] = useState(false);
  const [bookings, setBookings] = useState<BookingItem[]>(() => {
    try {
      const raw = sessionStorage.getItem('dash-bookings');
      if (!raw) return [];
      const { ts, items } = JSON.parse(raw) as { ts: number; items: BookingItem[] };
      if (Date.now() - ts > 10 * 60 * 1000) return [];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return items.map((b: any) => ({ ...b, startTime: new Date(b.startTime), endTime: new Date(b.endTime) }));
    } catch { return []; }
  });
  const [isLoadingBookings, setIsLoadingBookings] = useState<boolean>(() => {
    try {
      const raw = sessionStorage.getItem('dash-bookings');
      if (!raw) return true;
      const { ts } = JSON.parse(raw) as { ts: number };
      return Date.now() - ts > 10 * 60 * 1000;
    } catch { return true; }
  });
  const [bookingsError, setBookingsError] = useState<string | null>(null);
  const [recentActivities, setRecentActivities] = useState<Activity[]>([]);
  const [businessStats, setBusinessStats] = useState<BusinessStats>({
    newClients: 0,
    completionRate: 0,
    revenue: 0,
    growth: 0,
    bookingsCount: 0,
    customerRetentionRate: 0,
  });
  const [monthRevenue, setMonthRevenue] = useState<number | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);

  // State for current time to trigger re-filtering
  const [now, setNow] = useState(new Date());
  const [, setContactCacheReady] = useState(false);
  const [selectedBookingId, setSelectedBookingId] = useState<string | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<{
    title: string;
    categoryLabel: string;
    activities: Activity[];
  } | null>(null);

  // Helper functions
  const getContactAvatar = useCallback((booking: BookingItem) => {
    const contactId = booking.contact?.id;
    const name = booking.contact?.name;
    let avatarUrl = (contactId ? contactsCache.getAvatar(contactId) : undefined)
      || getContactAvatarUrl(booking.contact);

    // Only use avatarUrl if it's a valid string
    if (avatarUrl && typeof avatarUrl === 'string' && avatarUrl.trim() !== '') {
      avatarUrl = proxyImage(avatarUrl);
      return (
        <img
          src={avatarUrl}
          alt={name || tr('pages.dashboard.DashboardOwners.client')}
          className={styles.avatarImg}
          onError={(e) => {
            const imgElement = e.target as HTMLImageElement;
            const fallbackName = name || 'Client';
            imgElement.src = getDefaultAvatar(fallbackName);
          }}
        />
      );
    }

    // If we have a name, use default avatar
    if (name && typeof name === 'string' && name.trim() !== '') {
      return (
        <img
          src={getDefaultAvatar(name)}
          alt={name}
          className={styles.avatarImg}
        />
      );
    }

    // Last resort: show initial 'C' for Client
    return (
      <IonText className={styles.defaultAvatar}>
        C
      </IonText>
    );
  }, [tr]);

  const formatBookingTime = useCallback((startTime: string | Date) => {
    if (typeof startTime === 'string') {
      return new Date(`2023-01-01T${startTime}`).toLocaleString(intlLocale(), {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    }
    return startTime.toLocaleString(intlLocale(), {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  }, []);

  const fetchUpcomingBookings = useCallback(async () => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const sevenDays = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 7, 23, 59, 59, 999);

    setBookingsError(null);

    // 1. Firestore (IndexedDB → server) — primary source, no skeleton if data present
    try {
      const cached = await getBookingsFromCache(today, sevenDays);
      if (cached.length > 0) {
        setBookings(cached);
        setIsLoadingBookings(false);
        try {
          sessionStorage.setItem('dash-bookings', JSON.stringify({ ts: Date.now(), items: cached }));
        } catch { /* storage full */ }
        return;
      }
    } catch { /* fall through */ }

    // 2. Wix — only if Firestore returned nothing (skeleton shown during this wait)
    setIsLoadingBookings(true);
    try {
      const dataService = DataServiceFactory.getService();
      if (!dataService) throw new Error('DataService not available');

      const fresh = await dataService.bookings.getBookingsByDateRange(today, sevenDays);
      if (Array.isArray(fresh) && fresh.length > 0) {
        setBookings(fresh as BookingItem[]);
        try {
          sessionStorage.setItem('dash-bookings', JSON.stringify({ ts: Date.now(), items: fresh }));
        } catch { /* storage full */ }
      }
    } catch (error) {
      console.error('Error fetching upcoming bookings from Wix:', error);
      setBookingsError(tr('pages.dashboard.DashboardOwners.failedToLoadUpcomingBookingsPlease'));
    } finally {
      setIsLoadingBookings(false);
    }
  }, [tr]);


  useEffect(() => {
    // Bookings themselves are NOT fetched here on mount — the onSnapshot
    // subscription below (same query, same date range) already delivers
    // cached data immediately and server data shortly after, per its own
    // Firestore semantics. Calling fetchUpcomingBookings() here too ran a
    // second, redundant getDocs() against the identical query concurrently
    // with that listener's first snapshot, competing for the same
    // IndexedDB/network I/O for no additional data — confirmed via logcat
    // timing that content-ready time (draw-wait floor) tracked this
    // redundant fetch's completion, not the listener's (which settles
    // faster since it's already subscribed by the time this runs).
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    const dateStart = thirtyDaysAgo.toISOString().split('T')[0];

    const statsRefDate = new Date();
    const monthStart = new Date(statsRefDate.getFullYear(), statsRefDate.getMonth(), 1).toISOString().split('T')[0];
    const monthEnd = new Date(statsRefDate.getFullYear(), statsRefDate.getMonth() + 1, 0).toISOString().split('T')[0];

    // Fetch booking activities, invoice activities, stats, revenue, contacts
    // cache, and notification count all in parallel and commit their state
    // in one batch. These 6 were previously split across 3 separate mount
    // effects, each committing state independently as its own promise
    // resolved — 3 extra React re-renders on top of the ones bookings/auth
    // already need, confirmed via render-timing instrumentation (8 renders
    // total on a cold mount). None of these six depend on each other's
    // result, so there's no correctness reason for them to land in separate
    // renders — only contactsCache.subscribeToUpdates() (an ongoing
    // subscription, not a one-time fetch) and the notifications interval
    // stay in their own effects below, since those aren't one-shot mount
    // fetches and have their own cleanup lifecycle.
    const dataService = DataServiceFactory.getService();
    Promise.allSettled([
      dataService.analytics.getRecentActivities(),
      dataService.analytics.getBusinessStats(),
      getZohoInvoices({ date_start: dateStart }),
      getZohoProfitAndLossIncome({ date_start: monthStart, date_end: monthEnd }),
      contactsCache.ensureLoaded(),
    ]).then(([activitiesResult, statsResult, invoicesResult, revenueResult, contactsResult]) => {
      const bookingActivities = activitiesResult.status === 'fulfilled' ? activitiesResult.value : [];
      const invoices = invoicesResult.status === 'fulfilled' ? invoicesResult.value : [];
      setRecentActivities([...bookingActivities, ...invoices.map(zohoInvoiceToActivity)]);

      if (statsResult.status === 'fulfilled') {
        setBusinessStats(statsResult.value);
      }

      // Total Revenue = Zoho's own Profit & Loss report for the current month —
      // already nets out voided invoices, refunds, and credit notes correctly.
      setMonthRevenue(revenueResult.status === 'fulfilled' ? revenueResult.value : null);
      setStatsLoading(false);

      if (contactsResult.status === 'fulfilled') {
        setContactCacheReady(true);
      }
    });
  }, [fetchUpcomingBookings, user?.id]);

  useIonViewWillEnter(() => {
    fetchUpcomingBookings();
  });

  // Periodic refresh + BroadcastChannel for real-time updates
  useEffect(() => {
    const interval = setInterval(() => fetchUpcomingBookings(), 5 * 60 * 1000);

    const channel = new BroadcastChannel('booking-updates');
    channel.onmessage = (event) => {
      if (event.data.type === 'booking-changed') fetchUpcomingBookings();
    };

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'booking-refresh-trigger') fetchUpcomingBookings();
    };
    window.addEventListener('storage', handleStorageChange);

    return () => {
      clearInterval(interval);
      channel.close();
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [fetchUpcomingBookings]);

  // Real-time Firestore listener for upcoming bookings (fires on cache hit + server sync + any changes)
  useEffect(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const sevenDays = new Date(today); sevenDays.setDate(today.getDate() + 7); sevenDays.setHours(23, 59, 59, 999);

    const unsubscribe = subscribeToUpcomingBookings(
      today,
      sevenDays,
      (items) => {
        if (items.length > 0) {
          setBookings(items);
          setIsLoadingBookings(false);
          setBookingsError(null);
          try { sessionStorage.setItem('dash-bookings', JSON.stringify({ ts: Date.now(), items })); } catch { }
        }
      }
    );

    return unsubscribe;
  }, []);

  // Force update 'now' every minute to drop off past appointments
  useEffect(() => {
    const interval = setInterval(() => {
      setNow(new Date());
    }, 60000); // 1 minute
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    // Initial load is part of the batched Promise.allSettled above — this
    // effect only owns the ongoing subscription/cleanup.
    contactsCache.subscribeToUpdates();
    return () => contactsCache.unsubscribeFromUpdates();
  }, []);

  // Helper to get day, month, and weekday from YYYY-MM-DD string (static month, JS weekday)
  function getDateParts(dateString?: string) {
    if (!dateString) return { day: '', month: '', weekday: '' };
    const [year, month, day] = dateString.split('T')[0].split('-').map(Number);
    if (isNaN(year) || isNaN(month) || isNaN(day)) return { day: '', month: '', weekday: '' };
    const monthNames = getMonthNames('short');
    const weekdayNames = getWeekdayNames('short');
    const jsDate = new Date(year, month - 1, day);
    return {
      day: day,
      month: monthNames[month - 1],
      weekday: weekdayNames[jsDate.getDay()]
    };
  }

  // Helper to combine date and time as local (not UTC)
  function getLocalDateTime(dateStr: string, timeStr: string): Date {
    const [year, month, day] = dateStr.split('-').map(Number);
    const [h, m = 0, s = 0] = timeStr.split(':').map(Number);
    return new Date(year, month - 1, day, h, m, s);
  }

  // Sort bookings by startTime (ascending) before paginating, and filter out blocked times and canceled bookings
  const sortedBookings = bookings
    .filter(b => {
      // Only real bookings, not blocked or canceled
      if ((b.type && b.type !== 'booking') || String(b.status).toLowerCase() === 'cancelled' || String(b.status).toLowerCase() === 'canceled') {
        return false;
      }
      // Exclude any booking whose start time is in the past or now
      let start: Date;
      if (typeof b.startTime === 'string') {
        if (b.date && b.startTime.length <= 8) {
          start = getLocalDateTime(b.date, b.startTime);
        } else {
          start = new Date(b.startTime);
        }
      } else {
        start = b.startTime;
      }

      if (start <= now) {
        return false;
      }

      return true;
    })
    .slice()
    .sort((a, b) => {
      const aTime = typeof a.startTime === 'string' ? new Date(a.startTime).getTime() : a.startTime.getTime();
      const bTime = typeof b.startTime === 'string' ? new Date(b.startTime).getTime() : b.startTime.getTime();
      return aTime - bTime;
    });

  const BOOKINGS_PER_PAGE = 3;
  const totalPages = Math.ceil(sortedBookings.length / BOOKINGS_PER_PAGE);

  const goToAlerts = () => {
    router.push('/tabs/dashboard/owner/alerts');
  };


  const [activeShortcutIds, setActiveShortcutIds] = useState<string[]>(
    user?.preferences?.dashboardShortcuts || ['leads', 'analytics', 'profile', 'alerts', 'settings']
  );
  // Update shortcuts when user preferences change — computed during render
  // (React's "adjust state when a prop changes" pattern) rather than as a
  // setState call inside an effect.
  const [prevDashboardShortcuts, setPrevDashboardShortcuts] = useState(user?.preferences?.dashboardShortcuts);
  if (prevDashboardShortcuts !== user?.preferences?.dashboardShortcuts) {
    setPrevDashboardShortcuts(user?.preferences?.dashboardShortcuts);
    if (user?.preferences?.dashboardShortcuts) {
      setActiveShortcutIds(user.preferences.dashboardShortcuts);
    }
  }

  const toggleEditMode = () => setIsEditMode(true);

  // Don't load anything until auth is fully loaded and user preferences are
  // available. user.preferences is already whatever it's going to be by
  // the time this runs (gated on user existing + auth resolved) — the
  // previous 100ms setTimeout here didn't make preferences "more loaded",
  // it just delayed reading an already-available value, adding an extra
  // render + real latency to the dashboard's initial paint for no benefit.
  // Computed during render for the same reason as the sync above.
  const [prevShortcutsInitKey, setPrevShortcutsInitKey] = useState({ user, isLoading: authState.isLoading });
  if (prevShortcutsInitKey.user !== user || prevShortcutsInitKey.isLoading !== authState.isLoading) {
    setPrevShortcutsInitKey({ user, isLoading: authState.isLoading });
    if (user && !authState.isLoading) {
      if (user.preferences?.dashboardShortcuts && user.preferences.dashboardShortcuts.length > 0) {
        setActiveShortcutIds(user.preferences.dashboardShortcuts);
      } else {
        // Check localStorage as secondary fallback
        const localShortcuts = localStorage.getItem('dashboard-owner-shortcuts');
        let usedLocal = false;
        if (localShortcuts) {
          try {
            const parsedShortcuts = JSON.parse(localShortcuts);
            if (Array.isArray(parsedShortcuts) && parsedShortcuts.length > 0) {
              setActiveShortcutIds(parsedShortcuts);
              usedLocal = true;
            }
          } catch (error) {
            console.error('Error parsing local shortcuts:', error);
          }
        }
        // Only set defaults if no preferences exist anywhere
        if (!usedLocal) setActiveShortcutIds(['leads', 'analytics', 'profile', 'alerts', 'settings']);
      }
    }
  }

  const saveShortcutsToStorage = useCallback(async (shortcuts: string[]) => {
    try {
      // Save to server for cross-device sync
      await DataServiceFactory.getService().auth.updateUserPreferences({
        dashboardShortcuts: shortcuts
      });

      authContext?.actions?.updateLocalPreferences({ dashboardShortcuts: shortcuts });
      localStorage.setItem('dashboard-owner-shortcuts', JSON.stringify(shortcuts));
    } catch (error) {
      console.error('Failed to save shortcuts to server:', error);
      // Fallback to localStorage if server save fails
      localStorage.setItem('dashboard-owner-shortcuts', JSON.stringify(shortcuts));
    }
  }, [authContext]);

  // Storage listener for navigation triggers from modals
  useEffect(() => {
    const removeListener = DataStorageService.setupStorageListener((key) => {
      if (key === 'navigate-to-contact') {
        const contactId = DataStorageService.getData<string>('navigate-to-contact');
        if (contactId) {
          DataStorageService.removeData('navigate-to-contact');
          router.push(`/tabs/contacts/${contactId}`);
        }
      } else if (key === 'navigate-to-inbox') {
        DataStorageService.removeData('navigate-to-inbox');
        router.push('/team/messages');
      } else if (key.startsWith('navigate-to-booking-')) {
        const bookingId = DataStorageService.getData<string>(key);
        DataStorageService.removeData(key);
        if (bookingId) {
          router.push(`/tabs/booking/${bookingId}`);
        }
      }
    });

    return removeListener;
  }, [router]);

  const handleSaveFromModal = async (shortcuts: string[]) => {
    setActiveShortcutIds(shortcuts);
    await saveShortcutsToStorage(shortcuts);
  };

  const allShortcuts: Shortcut[] = OWNER_SHORTCUTS;
  const categoryNames = OWNER_CATEGORY_NAMES;


  // Computed values
  const activeShortcuts = activeShortcutIds
    .map(id => allShortcuts.find(s => s.id === id))
    .filter((shortcut): shortcut is Shortcut => shortcut !== undefined);


  const getActivityTypeIcon = (type: string) => {
    const t = type.toLowerCase();
    if (t.includes('payment') || t.includes('invoice') || t.includes('cash')) return receiptOutline;
    if (t.includes('overdue') || t.includes('due')) return alertCircle;
    return calendarOutline;
  };

  const BUBBLE_COLORS = [
    'var(--ion-color-primary)',
    'var(--ion-color-secondary)',
    'var(--ion-color-tertiary)',
    'var(--ion-color-success)',
    'var(--ion-color-warning)',
  ];
  const getBubbleColor = (initial?: string) => {
    const code = (initial || 'A').toUpperCase().charCodeAt(0);
    return BUBBLE_COLORS[code % BUBBLE_COLORS.length];
  };

  const getCategoryLabel = (title: string): string => {
    const t = title.toLowerCase();
    if (t.includes('payment') || t.includes('invoice') || t.includes('overdue')) return tr('pages.dashboard.DashboardOwners.financial');
    return tr('pages.dashboard.DashboardOwners.generalUpdates');
  };

  // Two-level grouping: broad category → fine-grained title
  const groupActivitiesByCategory = (activities: Activity[]): Record<string, Record<string, Activity[]>> => {
    const result: Record<string, Record<string, Activity[]>> = {};
    activities.forEach(activity => {
      const category = getCategoryLabel(activity.title);
      if (!result[category]) result[category] = {};
      if (!result[category][activity.title]) result[category][activity.title] = [];
      result[category][activity.title].push(activity);
    });
    return result;
  };

  return (
    <IonPage className={styles.page}>
      <IonHeader mode='ios' className="ion-no-border">
        <IonToolbar className={styles.dashboardToolbar}>
          <IonButton
            slot="start"
            fill="clear"
            className={styles.avatarButton}
            onClick={() => router.push('/tabs/profile')}
            title={tr('pages.dashboard.DashboardOwners.viewProfile')}
            aria-label={tr('pages.dashboard.DashboardOwners.viewProfile')}
          >
            <IonAvatar slot="icon-only" className={styles.userAvatar}>
              {user?.avatarUrl ? (
                <img
                  src={proxyImage(user.avatarUrl)}
                  alt={user?.name || ''}
                  onError={(e) => {
                    const imgElement = e.target as HTMLImageElement;
                    imgElement.src = getDefaultAvatar(user?.name || user?.email || 'User');
                  }}
                />
              ) : (
                <img
                  src={getDefaultAvatar(user?.name || user?.email || 'User')}
                  alt={user?.name || tr('pages.dashboard.DashboardOwners.user')}
                />
              )}
            </IonAvatar>
          </IonButton>
          <IonLabel>
            <h3 className={styles.welcomeText}>{tr('pages.dashboard.DashboardOwners.welcomeBack')}</h3>
            <h2 className={styles.userName}>
              {user?.firstName || // First try to use firstName if available
                (user?.name ? user.name.split(' ')[0] : tr('pages.dashboard.DashboardOwners.user')) // Otherwise split the full name
              }
            </h2>
          </IonLabel>
          <IonButtons slot="end">
            <IonButton
              className={styles.headerIconButton}
              onClick={() => router.push('/team/messages')}
              title={tr('pages.dashboard.DashboardOwners.teamMessaging')}
              aria-label={tr('pages.dashboard.DashboardOwners.teamMessaging')}
            >
              <IonIcon icon={chatbubblesOutline} slot="icon-only" />
            </IonButton>
            <IonItem lines="none" className={styles.alertsButtonWrapper}>
              <IonButton
                shape='round'
                className={styles.headerIconButton}
                onClick={goToAlerts}
                title={tr('pages.dashboard.DashboardOwners.alerts')}
                aria-label={tr('pages.dashboard.DashboardOwners.alerts')}
              >
                <IonIcon icon={notificationsOutline} slot="icon-only" />
              </IonButton>
              {unreadNotificationsCount > 0 && (
                <IonBadge color="danger" className={styles.notificationBadge}>
                  {unreadNotificationsCount > 99 ? '99+' : unreadNotificationsCount}
                </IonBadge>
              )}
            </IonItem>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent scrollY={false} className={styles.content}>
        <IonGrid className={styles.dashboardContainer}>
          {/* Menu Cards - native horizontal scroll (see .scrollContainer) */}
          <IonRow className={styles.scrollContainer}>
            <IonButton
              fill="clear"
              className={`${styles.menuCard} ${styles.editButton}`}
              onClick={toggleEditMode}
            >
              <IonLabel className={styles.menuCardContent}>
                <HiOutlinePencil className={styles.editIcon} />
              </IonLabel>
            </IonButton>

            <IonButton
              fill="clear"
              className={styles.menuCard}
              onClick={() => window.dispatchEvent(new CustomEvent('rs:openDialer', { detail: {} }))}
            >
              <IonLabel className={styles.menuCardContent}>
                <IonIcon icon={callOutline} className={styles.menuIcon} />
                <IonText className={styles.menuTitle}>{tr('pages.dashboard.DashboardOwners.call')}</IonText>
              </IonLabel>
            </IonButton>

            {activeShortcuts.map((shortcut) => (
              <IonButton
                key={shortcut.id}
                fill="clear"
                className={styles.menuCard}
                onClick={() => router.push(shortcut.route)}
              >
                <IonLabel className={styles.menuCardContent}>
                  <IonIcon icon={shortcut.icon} className={styles.menuIcon} />
                  <IonText className={styles.menuTitle}>{shortcut.title}</IonText>
                </IonLabel>
              </IonButton>
            ))}
          </IonRow>

          {/* Scrollable content with rounded corners */}
          <IonGrid key={scrollResetKey} ref={scrollableContentRef} className={styles.innerScrollContent}>
            {/* Upcoming Appointments Card */}
            <IonCard className={styles.welcomeCard}>
              <IonCardHeader>
                <IonCardTitle>{tr('pages.dashboard.DashboardOwners.upcomingBookings')}
                </IonCardTitle>
              </IonCardHeader>
              <IonCardContent>
                {bookingsError && (
                  <div className={styles.errorState}>
                    <IonIcon icon={warningOutline} />
                    <IonText>{bookingsError}</IonText>
                  </div>
                )}
                {isLoadingBookings ? (
                  <IonList role="list">
                    {Array.from({ length: 3 }).map((_, idx) => (
                      <IonItem key={`skeleton-${idx}`} className={styles.agendaItem} lines="none">
                        <IonCol size="auto" className={styles.dateCol}>
                          <IonSkeletonText animated style={{ width: '20px', height: '18px' }} />
                          <IonSkeletonText animated style={{ width: '24px', height: '12px' }} />
                        </IonCol>
                        <IonCol className={styles.bookingCol}>
                          <IonItem lines="none" className={styles.contactNameItem}>
                            <IonAvatar slot="start" className={styles.miniAvatar}>
                              <IonSkeletonText animated />
                            </IonAvatar>
                            <IonSkeletonText animated style={{ width: '120px', height: '16px' }} />
                          </IonItem>
                          <IonSkeletonText animated style={{ width: '80px', height: '14px' }} />
                          <IonSkeletonText animated style={{ width: '140px', height: '12px' }} />
                          <IonSkeletonText animated style={{ width: '100px', height: '12px' }} />
                        </IonCol>
                      </IonItem>
                    ))}
                  </IonList>
                ) : bookings.length === 0 && !bookingsError ? (
                  // Placeholder when no bookings and no error
                  <div className={styles.emptyState}>
                    <IonIcon icon={calendar} className={styles.emptyStateIcon} />
                    <IonText className={styles.emptyStateTitle}>
                      <h3>{tr('pages.dashboard.DashboardOwners.noUpcomingBookings')}</h3>
                    </IonText>
                    <IonText className={styles.emptyStateSubtitle}>
                      <p>{tr('pages.dashboard.DashboardOwners.yourScheduleIsClearForNow')}</p>
                    </IonText>
                  </div>
                ) : bookings.length === 0 && bookingsError ? (
                  // Error state without retry button
                  <div className={styles.emptyState}>
                    <IonIcon icon={warningOutline} className={styles.emptyStateIcon} />
                    <IonText className={styles.emptyStateTitle}>
                      <h3>{tr('pages.dashboard.DashboardOwners.unableToLoadBookings')}</h3>
                    </IonText>
                    <IonText className={styles.emptyStateSubtitle}>
                      <p>{tr('pages.dashboard.DashboardOwners.automaticRetryInProgress')}</p>
                    </IonText>
                  </div>
                ) : (
                  // Existing Swiper content when bookings exist
                  <Swiper
                    modules={[Pagination]}
                    pagination={{ clickable: true }}
                    className={styles.agendaSlides}
                    style={{ paddingBottom: '1.5rem' }}
                    spaceBetween={15}
                  >
                    {Array.from({ length: totalPages }).map((_, idx) => (
                      <SwiperSlide key={idx}>
                        <IonList role="list">
                          {sortedBookings
                            .slice(idx * BOOKINGS_PER_PAGE, (idx + 1) * BOOKINGS_PER_PAGE)
                            .map(booking => (
                              <IonItem
                                key={booking.id}
                                className={styles.agendaItem}
                                lines="none"
                                detail={false}
                                button
                                onClick={() => setSelectedBookingId(booking.id)}
                                role="listitem"
                              >
                                <IonCol size="auto" className={styles.dateCol}>
                                  <IonText className={styles.dayNumber}>
                                    {getDateParts(booking.date).day}
                                  </IonText>
                                  <IonText className={styles.monthText}>
                                    {getDateParts(booking.date).month}
                                  </IonText>
                                </IonCol>

                                <IonCol className={styles.bookingCol}>
                                  <IonItem lines="none" className={styles.contactNameItem}>
                                    <IonAvatar slot="start" className={styles.miniAvatar}>
                                      {getContactAvatar(booking)}
                                    </IonAvatar>
                                    <IonText className={styles.contactName}>
                                      {booking.contact?.name || tr('pages.dashboard.DashboardOwners.clientName')}
                                    </IonText>
                                  </IonItem>

                                  {/* Service type below contact name */}
                                  <IonText
                                    className={styles.serviceType}
                                    style={{
                                      color: getServiceColor(booking.serviceType as ServiceType)
                                    }}
                                  >
                                    {translateDynamic(booking.serviceType) || tr('pages.dashboard.DashboardOwners.service')}
                                  </IonText>

                                  {/* Day and time below service type */}
                                  <IonText className={styles.dayTimeLine}>
                                    {getDateParts(booking.date).weekday} • {
                                      booking.startTime ? formatBookingTime(booking.startTime) : tr('pages.dashboard.DashboardOwners.1200Pm')
                                    } • {booking.duration || '1h'}
                                  </IonText>

                                  {/* Location and staff at bottom */}
                                  <IonText className={styles.locationLine}>
                                    {booking.contact?.address || tr('pages.dashboard.DashboardOwners.noLocation')}
                                  </IonText>
                                  <IonText className={styles.staffLine}>
                                    {booking.staff || tr('pages.dashboard.DashboardOwners.unassignedStaff')}{booking.resource ? ` • ${booking.resource}` : ''}
                                  </IonText>
                                </IonCol>
                              </IonItem>
                            ))}
                        </IonList>
                      </SwiperSlide>
                    ))}
                  </Swiper>
                )}
              </IonCardContent>
            </IonCard>

            {/* Business Overview Card */}
            <IonCard className={styles.overviewCard}>
              <IonCardHeader>
                <IonCardTitle>{tr('pages.dashboard.DashboardOwners.businessOverview')}
                </IonCardTitle>
                <IonCardSubtitle className={styles.overviewSubtitle}>{tr('pages.dashboard.DashboardOwners.thisMonth')}</IonCardSubtitle>
              </IonCardHeader>
              <IonCardContent>
                <IonGrid className={styles.metricsGrid}>
                  <IonRow>
                    <IonCol size="6">
                      <IonItem lines="none" className={styles.metricItem}>
                        <IonIcon icon={cash} slot="start" className={styles.metricIcon} />
                        <IonLabel className={styles.metricBody}>
                          {statsLoading ? (
                            <IonSkeletonText animated className={styles.metricValueSkeleton} />
                          ) : (
                            <h3 className={styles.metricValue}>{monthRevenue === null ? '—' : `$${monthRevenue.toLocaleString()}`}</h3>
                          )}
                          <p className={styles.metricLabel}>{tr('pages.dashboard.DashboardOwners.totalRevenue')}</p>
                        </IonLabel>
                      </IonItem>
                    </IonCol>

                    <IonCol size="6">
                      <IonItem lines="none" className={styles.metricItem}>
                        <IonIcon icon={calendar} slot="start" className={styles.metricIcon} />
                        <IonLabel className={styles.metricBody}>
                          {statsLoading ? (
                            <IonSkeletonText animated className={styles.metricValueSkeleton} />
                          ) : (
                            <h3 className={styles.metricValue}>{businessStats.bookingsCount}</h3>
                          )}
                          <p className={styles.metricLabel}>{tr('pages.dashboard.DashboardOwners.totalBookings')}</p>
                        </IonLabel>
                      </IonItem>
                    </IonCol>

                    <IonCol size="6">
                      <IonItem lines="none" className={styles.metricItem}>
                        <IonIcon icon={personAdd} slot="start" className={styles.metricIcon} />
                        <IonLabel className={styles.metricBody}>
                          {statsLoading ? (
                            <IonSkeletonText animated className={styles.metricValueSkeleton} />
                          ) : (
                            <h3 className={styles.metricValue}>{businessStats.newClients}</h3>
                          )}
                          <p className={styles.metricLabel}>{tr('pages.dashboard.DashboardOwners.newClients')}</p>
                        </IonLabel>
                      </IonItem>
                    </IonCol>

                    <IonCol size="6">
                      <IonItem lines="none" className={styles.metricItem}>
                        <IonIcon icon={checkmarkCircle} slot="start" className={styles.metricIcon} />
                        <IonLabel className={styles.metricBody}>
                          {statsLoading ? (
                            <IonSkeletonText animated className={styles.metricValueSkeleton} />
                          ) : (
                            <h3 className={styles.metricValue}>{businessStats.completionRate}%</h3>
                          )}
                          <p className={styles.metricLabel}>{tr('pages.dashboard.DashboardOwners.completionRate')}</p>
                        </IonLabel>
                      </IonItem>
                    </IonCol>

                    <IonCol size="6">
                      <IonItem lines="none" className={styles.metricItem}>
                        <IonIcon icon={trendingUp} slot="start" className={styles.metricIcon} />
                        <IonLabel className={styles.metricBody}>
                          {statsLoading ? (
                            <IonSkeletonText animated className={styles.metricValueSkeleton} />
                          ) : (
                            <h3 className={styles.metricValue}>{businessStats.customerRetentionRate}%</h3>
                          )}
                          <p className={styles.metricLabel}>{tr('pages.dashboard.DashboardOwners.customerRetention')}</p>
                        </IonLabel>
                      </IonItem>
                    </IonCol>

                    <IonCol size="6">
                      <IonItem lines="none" className={styles.metricItem}>
                        <IonIcon icon={barChart} slot="start" className={styles.metricIcon} />
                        <IonLabel className={styles.metricBody}>
                          {statsLoading ? (
                            <IonSkeletonText animated className={styles.metricValueSkeleton} />
                          ) : (
                            <h3 className={styles.metricValue}>{businessStats.growth > 0 ? '+' : ''}{businessStats.growth}%</h3>
                          )}
                          <p className={styles.metricLabel}>{tr('pages.dashboard.DashboardOwners.growth')}</p>
                        </IonLabel>
                      </IonItem>
                    </IonCol>
                  </IonRow>
                </IonGrid>
              </IonCardContent>
            </IonCard>

            {/* Separate Recent Activity header using IonItem */}
            <IonItem lines="none" className={styles.recentActivityHeader}>
              <IonLabel className={styles.recentActivityTitle}>{tr('pages.dashboard.DashboardOwners.recentActivity')}
              </IonLabel>
            </IonItem>

            {/* Activity content */}
            <div>
              {recentActivities.length === 0 ? (
                <IonItem lines="none" className={styles.noActivityMessage}>
                  <IonLabel>{tr('pages.dashboard.DashboardOwners.noRecentActivity')}</IonLabel>
                </IonItem>
              ) : (
                Object.entries(groupActivitiesByCategory(recentActivities)).map(([categoryName, titleGroups]) => (
                  <div key={categoryName}>
                    <div className={styles.activitySectionLabel}>{categoryName}</div>
                    {Object.entries(titleGroups).map(([, group]) => {
                      const latest = group[0];
                      const moreCount = group.length - 1;
                      const visibleMore = group.slice(1, 4);

                      const handleItemTap = () => {
                        if (latest.id.startsWith('zoho-')) {
                          const invoiceId = latest.id.replace(/^zoho-(paid|overdue|invoice|draft|sent|viewed|void)-/, '');
                          router.push(`/tabs/invoice/${invoiceId}`);
                        } else {
                          const bookingId = latest.id.replace(/^(cancel|complete|overdue|confirm|booking)-/, '');
                          setSelectedBookingId(bookingId);
                        }
                      };

                      return (
                        <IonCard key={latest.id} className={styles.activityGroupCard}>
                          <div
                            className={styles.activityMainRow}
                            onClick={handleItemTap}
                            style={{ cursor: 'pointer' }}
                          >
                            <div className={styles.activityIconWrapper}>
                              <div className={styles.activityTypeCircle}>
                                <IonIcon icon={getActivityTypeIcon(latest.type)} className={styles.activityTypeIc} />
                              </div>
                              <div
                                className={styles.activityUserBadge}
                                style={{ background: getBubbleColor(latest.userInitial) }}
                              >
                                {latest.userInitial || '?'}
                              </div>
                            </div>
                            <div className={styles.activityContent}>
                              <span className={styles.activityTitle}>{localizeActivity(latest).title}</span>
                              <span className={styles.activityDetail}>{localizeActivity(latest).detail}</span>
                            </div>
                            <div className={styles.activityRightCol}>
                              <span className={styles.activityTimestamp}>{localizeActivity(latest).time}</span>
                              <IonIcon icon={chevronForward} className={styles.activityChevron} />
                            </div>
                          </div>
                          {moreCount > 0 && (
                            <div
                              className={styles.showMoreInCard}
                              onClick={() => setSelectedGroup({ title: latest.title, categoryLabel: categoryName, activities: group })}
                              style={{ cursor: 'pointer' }}
                            >
                              <span className={styles.showMoreLink}>{tr('desktop.pages.dashboard.owner.DashboardOwnersDesktop.showMoreLikeThis', { moreCount, v2: translateDynamic(moreCount === 1 ? 'update' : 'updates') })}
                              </span>
                              <div className={styles.moreAvatarGroup}>
                                {visibleMore.map((act, idx) => (
                                  <div
                                    key={idx}
                                    className={styles.moreAvatarBubble}
                                    style={{
                                      background: getBubbleColor(act.userInitial),
                                      zIndex: 10 - idx,
                                      marginLeft: idx === 0 ? 0 : -8,
                                    }}
                                  >
                                    {act.userInitial || '?'}
                                  </div>
                                ))}
                                {moreCount > 3 && (
                                  <div className={styles.moreAvatarOverflow} style={{ zIndex: 6, marginLeft: -8 }}>
                                    +{moreCount - 3}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </IonCard>
                      );
                    })}
                  </div>
                ))
              )}
            </div>

            {/* Edit Shortcuts Modal */}
            <IonModal
              isOpen={isEditMode}
              onDidDismiss={() => setIsEditMode(false)}
              className={styles.editModal}
            >
              <ShortcutsEditModal
                onDismiss={() => setIsEditMode(false)}
                activeShortcutIds={activeShortcutIds}
                allShortcuts={allShortcuts}
                categoryNames={categoryNames}
                onSave={handleSaveFromModal}
              />
            </IonModal>
          </IonGrid>
        </IonGrid>
      </IonContent>


      {/* Activity Group Page */}
      <ActivityGroupPage
        isOpen={!!selectedGroup}
        onClose={() => setSelectedGroup(null)}
        title={selectedGroup?.title ?? ''}
        categoryLabel={selectedGroup?.categoryLabel ?? ''}
        activities={selectedGroup?.activities ?? []}
        getBubbleColor={getBubbleColor}
      />

      <BookingDetails
        isOpen={!!selectedBookingId}
        onClose={() => setSelectedBookingId(null)}
        bookingId={selectedBookingId || undefined}
      />
    </IonPage>
  );
};

export default memo(DashboardOwners);

